import React, { useState, useEffect, useMemo } from 'react';
import { Tag, X, Hash, Search, Filter, Clock, Cloud, Sun, Users, Footprints } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { TimeSlotSelector } from './TimeSlotSelector';
import { useApp } from '../../context/AppContext';
import {
  WEATHER_OPTIONS, LIGHT_OPTIONS, CROWD_LEVEL_OPTIONS,
  DIFFICULTY_OPTIONS, TIME_WINDOW_OPTIONS
} from '../../constants/spotEnums';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';

// Icon mapping for categories
const getCategoryIcon = (iconName) => {
  const icons = {
    'Mountain': '🏔️',
    'Activity': '🏃',
    'Sun': '☀️',
    'Navigation': '🧭',
    'Heart': '💖'
  };
  return icons[iconName] || '🏷️';
};

// Catégories système V2 (métadonnées)
const SYSTEM_CATEGORIES = [
  { id: 'time_windows', name: 'Créneaux', icon: '⏰', options: TIME_WINDOW_OPTIONS, color: '#f59e0b' },
  { id: 'weather', name: 'Météo', icon: '☀️', options: WEATHER_OPTIONS, color: '#0ea5e9' },
  { id: 'light', name: 'Lumière', icon: '💡', options: LIGHT_OPTIONS, color: '#f97316' },
  { id: 'crowd_level', name: 'Affluence', icon: '👥', options: CROWD_LEVEL_OPTIONS, color: '#22c55e' },
  { id: 'difficulty', name: 'Accessibilité', icon: '🚶', options: DIFFICULTY_OPTIONS, color: '#a855f7' },
];

export function TagSelector({
  selectedTags = [], // Array of selected tag slugs
  selectedTimeSlots = [], // Array of selected time slot IDs
  onTagsChange = () => {},
  onTimeSlotsChange = () => {},
  tagCategories = [],
  allTags = [],
  searchQuery = '',
  onSearchChange = () => {},
  disabled = false,
  showSelectedTags = true,
  placeholder = "Rechercher des tags..."
}) {
  const { state, actions } = useApp();
  const { selectedMetadataFilters } = state;
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [expandedSystemCategories, setExpandedSystemCategories] = useState({});

  // Compter le total des filtres (tags + métadonnées)
  const totalMetadataFilters = Object.values(selectedMetadataFilters).flat().length;
  const totalFilters = selectedTags.length + selectedTimeSlots.length + totalMetadataFilters;

  // Toggle une catégorie système
  const toggleSystemCategory = (categoryId) => {
    setExpandedSystemCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Toggle un filtre de métadonnée
  const toggleMetadataFilter = (e, categoryId, value) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Toggle metadata filter:', categoryId, value);
    actions.toggleMetadataFilter(categoryId, value);
  };

  // Filter tags based on search input - using useMemo instead of useEffect
  const filteredTags = useMemo(() => {
    let filteredList = [];
    
    if (searchInput.trim()) {
      const query = searchInput.toLowerCase();
      filteredList = allTags.filter(tag => 
        tag.name.toLowerCase().includes(query) ||
        tag.synonyms?.some(synonym => synonym.toLowerCase().includes(query))
      );
    } else {
      filteredList = [...allTags];
    }

    // Sort: selected tags first, then unselected tags
    // Within each group, sort by name alphabetically
    return filteredList.sort((a, b) => {
      const aSelected = selectedTags.includes(a.slug);
      const bSelected = selectedTags.includes(b.slug);
      
      // If selection status is different, selected comes first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      
      // If both have same selection status, sort alphabetically
      return a.name.localeCompare(b.name, 'fr');
    });
  }, [searchInput, allTags, selectedTags]);

  // Get tag object from slug
  const getTagBySlug = (slug) => {
    return allTags.find(tag => tag.slug === slug);
  };

  // Get category for a tag
  const getCategoryForTag = (tag) => {
    return tagCategories.find(cat => cat.id === tag.category_id);
  };

  // Handle tag selection/deselection
  const handleTagToggle = (tagSlug) => {
    if (selectedTags.includes(tagSlug)) {
      // Remove tag (deselect)
      onTagsChange(selectedTags.filter(slug => slug !== tagSlug));
    } else {
      // Add tag (select)
      onTagsChange([...selectedTags, tagSlug]);
    }
  };

  // Handle search input change
  const handleSearchInputChange = (value) => {
    setSearchInput(value);
    onSearchChange(value);
  };

  // Handle hashtag detection and auto-completion
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && filteredTags.length > 0) {
      e.preventDefault();
      const firstTag = filteredTags[0];
      handleTagToggle(firstTag.slug);
      setSearchInput('');
    }
  };

  return (
    <div className="flex flex-col space-y-2">
      {/* Search Input with Tag Button */}
      <div className="flex items-center space-x-2">
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={disabled}
              className="flex items-center space-x-2 text-xs"
            >
              <Tag size={14} />
              <span>Filtres</span>
              {totalFilters > 0 && (
                <span className="bg-blue-500 text-white rounded-full text-xs px-2 py-0.5 ml-1">
                  {totalFilters}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[450px] max-h-[500px]">
            {/* Time Slot Selector */}
            <TimeSlotSelector
              selectedTimeSlots={selectedTimeSlots}
              onTimeSlotsChange={onTimeSlotsChange}
              disabled={disabled}
            />

            {/* ==================== CATÉGORIES SYSTÈME V2 ==================== */}
            <div className="p-3 border-b bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                <Filter size={12} className="mr-1" />
                Filtres essentiels
                {totalMetadataFilters > 0 && (
                  <span className="ml-2 bg-blue-500 text-white rounded-full px-2 py-0.5 text-xs">
                    {totalMetadataFilters}
                  </span>
                )}
              </div>
              
              <div className="space-y-2">
                {SYSTEM_CATEGORIES.map((category) => {
                  const selectedInCategory = selectedMetadataFilters[category.id]?.length || 0;
                  const isExpanded = expandedSystemCategories[category.id];
                  
                  return (
                    <div key={category.id} className="bg-white rounded-lg border border-gray-100">
                      {/* Category Header */}
                      <button
                        onClick={() => toggleSystemCategory(category.id)}
                        className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <span>{category.icon}</span>
                          <span className="text-sm font-medium">{category.name}</span>
                          {selectedInCategory > 0 && (
                            <span 
                              className="text-xs px-1.5 py-0.5 rounded-full text-white"
                              style={{ backgroundColor: category.color }}
                            >
                              {selectedInCategory}
                            </span>
                          )}
                        </div>
                        <span className="text-gray-400 text-xs">
                          {isExpanded ? '▲' : '▼'}
                        </span>
                      </button>
                      
                      {/* Category Options */}
                      {isExpanded && (
                        <div className="px-2 pb-2 flex flex-wrap gap-1.5">
                          {category.options.map((option) => {
                            const isSelected = selectedMetadataFilters[category.id]?.includes(option.value);
                            return (
                              <button
                                key={option.value}
                                onClick={(e) => toggleMetadataFilter(e, category.id, option.value)}
                                className={`
                                  inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs
                                  transition-all duration-200 border
                                  ${isSelected 
                                    ? 'border-current ring-2 ring-offset-1 ring-current ring-opacity-30 font-medium' 
                                    : 'border-gray-200 hover:border-gray-300'
                                  }
                                `}
                                style={isSelected ? { 
                                  backgroundColor: `${category.color}20`,
                                  color: category.color,
                                  borderColor: category.color
                                } : {}}
                              >
                                <span>{option.icon}</span>
                                <span>{option.label}</span>
                                {isSelected && <span>✓</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Clear metadata filters button */}
              {totalMetadataFilters > 0 && (
                <button
                  onClick={() => actions.clearMetadataFilters()}
                  className="mt-2 text-xs text-red-500 hover:text-red-700 hover:underline"
                >
                  Effacer les filtres essentiels
                </button>
              )}
            </div>

            {/* ==================== TAGS CLASSIQUES ==================== */}
            {/* Search Input */}
            <div className="p-3 border-b sticky top-0 bg-white">
              <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                <Hash size={12} className="mr-1" />
                Tags de type
              </div>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder={placeholder}
                  value={searchInput}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="pl-9 text-sm"
                  autoFocus
                />
              </div>
              {(selectedTags.length > 0 || selectedTimeSlots.length > 0 || totalMetadataFilters > 0) && (
                <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                  <div>
                    {totalMetadataFilters > 0 && (
                      <span>{totalMetadataFilters} filtre{totalMetadataFilters > 1 ? 's' : ''} essentiel{totalMetadataFilters > 1 ? 's' : ''}</span>
                    )}
                    {selectedTags.length > 0 && (
                      <span>
                        {totalMetadataFilters > 0 ? ' + ' : ''}
                        {selectedTags.length} tag{selectedTags.length > 1 ? 's' : ''}
                      </span>
                    )}
                    {selectedTimeSlots.length > 0 && (
                      <span>
                        {(selectedTags.length > 0 || totalMetadataFilters > 0) ? ' + ' : ''}
                        {selectedTimeSlots.length} période{selectedTimeSlots.length > 1 ? 's' : ''}
                      </span>
                    )}
                    {' '}sélectionné{(totalFilters) > 1 ? 's' : ''}
                  </div>
                  <div className="flex space-x-2">
                    {selectedTags.length > 0 && (
                      <button
                        onClick={() => onTagsChange([])}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        Effacer tags
                      </button>
                    )}
                    {selectedTimeSlots.length > 0 && (
                      <button
                        onClick={() => onTimeSlotsChange([])}
                        className="text-purple-600 hover:text-purple-800 hover:underline"
                      >
                        Effacer périodes
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* All Tags Grid - Scrollable */}
            <div className="max-h-80 overflow-y-auto p-3">
              {/* Selected tags section */}
              {filteredTags.filter(tag => selectedTags.includes(tag.slug)).length > 0 && !searchInput.trim() && (
                <div className="mb-4">
                  <div className="text-xs font-medium text-gray-600 mb-2 flex items-center">
                    <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full mr-2">✓</span>
                    Tags sélectionnés
                  </div>
                  <div className="grid grid-cols-2 gap-2 pb-3 border-b border-gray-100">
                    {filteredTags
                      .filter(tag => selectedTags.includes(tag.slug))
                      .map((tag) => {
                        const category = getCategoryForTag(tag);
                        const isSelected = selectedTags.includes(tag.slug);
                        
                        return (
                          <button
                            key={`selected-${tag.id}`}
                            onClick={() => handleTagToggle(tag.slug)}
                            className="flex items-center justify-between p-2 rounded-lg text-left transition-all duration-200 border-2 shadow-md bg-blue-50"
                            style={{ 
                              backgroundColor: `${category?.color}15`, 
                              borderColor: category?.color,
                              color: category?.color
                            }}
                          >
                            <div className="flex-1">
                              <div className="flex items-center space-x-1">
                                <Hash size={10} className="text-current" />
                                <span className="text-sm font-medium">{tag.name}</span>
                                <span className="text-xs">✓</span>
                              </div>
                              <div className="text-xs opacity-70 mt-1">
                                {category?.name || 'Non catégorisé'}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* All tags section */}
              <div className={filteredTags.filter(tag => selectedTags.includes(tag.slug)).length > 0 && !searchInput.trim() ? "mt-4" : ""}>
                {!searchInput.trim() && filteredTags.filter(tag => !selectedTags.includes(tag.slug)).length > 0 && (
                  <div className="text-xs font-medium text-gray-600 mb-2">
                    Autres tags disponibles
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-2">
                  {(searchInput.trim() ? filteredTags : filteredTags.filter(tag => !selectedTags.includes(tag.slug)))
                    .map((tag) => {
                      const category = getCategoryForTag(tag);
                      const isSelected = selectedTags.includes(tag.slug);
                      
                      return (
                        <button
                          key={`unselected-${tag.id}`}
                          onClick={() => handleTagToggle(tag.slug)}
                          className={`flex items-center justify-between p-2 rounded-lg text-left transition-all duration-200 border ${
                            isSelected 
                              ? 'border-2 shadow-md transform scale-105'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          style={isSelected ? { 
                            backgroundColor: `${category?.color}15`, 
                            borderColor: category?.color,
                            color: category?.color
                          } : {}}
                        >
                          <div className="flex-1">
                            <div className="flex items-center space-x-1">
                              <Hash size={10} className={isSelected ? 'text-current' : 'text-blue-500'} />
                              <span className="text-sm font-medium">{tag.name}</span>
                              {isSelected && (
                                <span className="text-xs">✓</span>
                              )}
                            </div>
                            <div className="text-xs opacity-70 mt-1">
                              {category?.name || 'Non catégorisé'}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>
              </div>
              
              {filteredTags.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Tag size={24} className="mx-auto mb-2 opacity-50" />
                  <div className="text-sm">Aucun tag trouvé</div>
                  {searchInput.trim() && (
                    <div className="text-xs mt-1">
                      pour &quot;{searchInput}&quot;
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer with stats */}
            <div className="border-t p-2 text-xs text-gray-500 bg-gray-50">
              {filteredTags.length} tag{filteredTags.length > 1 ? 's' : ''} disponible{filteredTags.length > 1 ? 's' : ''}
              {searchInput.trim() && ` (filtré${filteredTags.length > 1 ? 's' : ''})`}
              {selectedTimeSlots.length > 0 && (
                <span className="ml-2">• {selectedTimeSlots.length} période{selectedTimeSlots.length > 1 ? 's' : ''}</span>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}